import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { AREA_SCHEMA, AREA_CLAIM_SCHEMA, MAPPED_AREA_SCHEMA } from '../schema/areaSchema';
import {
  GOLDEN_AREA_AND_CLAIMS,
  SAVE_AREA_CLAIM,
  OVERRIDE_AREA_CLAIM,
  INSERT_GOLDEN_AREA,
} from '../hasura/queries/area/areaClaims';

import { VIEW_AREA_MAPPINGS, DELETE_AREA_MAPPINGS } from '../hasura/queries/area/areaMapping';
import { DELETE_AREA } from '../hasura/queries/area/areas';
import { ClaimFilters } from '../components/claim/claimFilters';

import { Progress } from '../components/util/progress';

import { Error } from '../components/util/notifications';

import MappingView from '../components/mapping/mappingView';
import ClaimHistoryTable from '../components/table/claimHistoryTable';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenAreaView = ({ areaId, isNewArea }) => {
  const ignoreEverything = {};
  AREA_SCHEMA.forEach(row => {
    ignoreEverything[row.columnKey] = 'Ignored';
  });

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewArea ? ignoreEverything : {});
  const [overrides, setOverrides] = useState({});
  //new area mode has a 0 id
  const [claimMode, setClaimMode] = useState(isNewArea ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);

  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_AREA_AND_CLAIMS, {
    variables: {
      area_id: areaId,
    },
  });

  if (loading) return <Progress />;

  let allClaims = [];
  data &&
    data.statsbomb_area &&
    data.statsbomb_area[0] &&
    data.statsbomb_area[0].mapping_areas.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_areas];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }

  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_area_uuid === claimUUID;
    })[0];
    if (claim) {
      AREA_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    AREA_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(ignoreEverything);
  };

  const getGoldenAreaId = insertedArea => insertedArea?.data?.insert_statsbomb_area?.returning?.[0]?.area_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_area_uuid);
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <h2>Area Singular: {isNewArea ? ' New Area' : areaId}</h2>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="area"
              schema={AREA_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{ headerValue: 'Area Name', bodyValue: data.statsbomb_area[0]?.area_name }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <MappingView
            type="area"
            recordId={areaId}
            schema={MAPPED_AREA_SCHEMA}
            subscription={VIEW_AREA_MAPPINGS}
            deleteMutation={DELETE_AREA_MAPPINGS}
            deleteEntityMutation={DELETE_AREA}
            entityKey="provider_area_id"
            styles={{ tableContatinerClasses, buttonClasses }}
          />
        )}

        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Area Attributes {selectedClaimId && ' And Claim Values'}</h3>

          <SingleClaimTable
            goldenEntity={data.statsbomb_area[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_area_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={AREA_SCHEMA}
            saveMutation={SAVE_AREA_CLAIM}
            overrideMutation={OVERRIDE_AREA_CLAIM}
            createMutation={INSERT_GOLDEN_AREA}
            sourcePK="provider_area_id"
            claimPK="claim_area_uuid"
            goldenEntityPK="area_id"
            getGoldenEntityId={getGoldenAreaId}
            entityType="area"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

function Area(props) {
  const query = new URLSearchParams(useLocation().search);
  const isNewArea = query.get('new');
  const areaId = isNewArea ? '0' : query.get('area_id');

  return <GoldenAreaView areaId={areaId} isNewArea={!!isNewArea} />;
}

export default Area;
